/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1653177600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Sun May 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "microsoft-teams",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Sending Teams messages (added / changed / removed)",
          "depth": 0,
          "id": "sending-teams-messages-added--changed--removed"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection"
        }, {
          "value": "Added, changed, and removed messages",
          "depth": 1,
          "id": "added-changed-and-removed-messages"
        }, {
          "value": "Sending Teams messages (all results)",
          "depth": 0,
          "id": "sending-teams-messages-all-results"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection-1"
        }, {
          "value": "Subject and body content",
          "depth": 1,
          "id": "subject-and-body-content"
        }, {
          "value": "Templating messages",
          "depth": 0,
          "id": "templating-messages"
        }, {
          "value": "Liquid templating",
          "depth": 1,
          "id": "liquid-templating"
        }, {
          "value": "Table",
          "depth": 0,
          "id": "table"
        }, {
          "value": "Channel selection",
          "depth": 1,
          "id": "channel-selection-2"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Need admin approval",
          "depth": 1,
          "id": "need-admin-approval"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      img: "img",
      ul: "ul",
      a: "a",
      strong: "strong",
      pre: "pre",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Microsoft Teams destination, Hightouch can serve as a notifier for various changes. This is a very flexible integration, but here are a few example use cases:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Notify your customer success teams when product usage for a key account suddenly drops"
        }), "\n", _jsx(_components.li, {
          children: "Notify an account executive when product usage surpasses a certain threshold"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary of the number of sign ups, compared to the same day last month"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary on the adoption of a core feature"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sending-teams-messages-added--changed--removed",
        children: "Sending Teams messages (added / changed / removed)"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch will not send any messages on the initial run to prevent a flood of\nmessages to your channel. Messages will start after the this initial sync."
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports sending messages based on rows that have been added, changed, or removed since the last sync run. Each row is sent as an individual message and can be sent to different channels and templated with Liquid."
      }), "\n", _jsx(_components.p, {
        children: "Message formatting is best when the following conditions are met:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "You want to write a query, and have Hightouch detect the added and removed rows"
        }), "\n", _jsx(_components.li, {
          children: "You want a custom message sent per each added and removed rows"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports both sending messages to a specific channel that the Hightouch bot is part of, or by pulling the channel ID each row. Enable the \"Use column\" toggle to dynamically set the target channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-teams-channel-column.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "added-changed-and-removed-messages",
        children: "Added, changed, and removed messages"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to template the content you would like to send in your message."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "When a row is added in your query results, Hightouch will send the row added content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is changed in your query results, Hightouch will send the row changed content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is deleted in your query results, Hightouch will send the row deleted content."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-teams-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sending-teams-messages-all-results",
        children: "Sending Teams messages (all results)"
      }), "\n", _jsx(_components.p, {
        children: "The batch message mode is similar to the normal message mode, except it sends all rows in the query results rather than only added, changed, and removed rows since the last sync."
      }), "\n", _jsx(_components.p, {
        children: "This mode also allows you to specify how many rows are sent per message, which can range from 1 to 20. With 1 row per message, it would be similar to the message mode, whereas 20 rows per message would be similar to a tabular representation."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch push a message containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows, formatted as a message, to Microsoft Teams"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added, changed, and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection-1",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your messages to a single channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-teams-channel.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "subject-and-body-content",
        children: "Subject and body content"
      }), "\n", _jsx(_components.p, {
        children: "For each message, Hightouch allows setting the subject and row content. Only the row content is required. The row content is repeated per row in the message and has access to the row values via templating."
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-teams-batch-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "templating-messages",
        children: "Templating messages"
      }), "\n", _jsx(_components.h3, {
        id: "liquid-templating",
        children: "Liquid templating"
      }), "\n", _jsxs(_components.p, {
        children: ["For both the message and batch message modes, ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid templating "
        }), "is supported. The most important implication of this is that columns from the added or removed row can be accessed as part of the message body. Here is an example message making use of Liquid to insert a column into the message."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "User with name {{ row[\"name\"] }} and email {{ row[\"email\"] }} has signed up\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "table",
        children: "Table"
      }), "\n", _jsx(_components.p, {
        children: "Table formatting sends all of your query results formatted as a text table as messages to Microsoft Teams. This mode is recommended only for small sets of data."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch push a message containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows, formatted as a table, to Microsoft Teams"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "channel-selection-2",
        children: "Channel selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your messages to a single channel."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-teams-channel.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "need-admin-approval",
        children: "Need admin approval"
      }), "\n", _jsxs(_components.p, {
        children: ["If you see a ", _jsx(_components.strong, {
          children: "Need admin approval"
        }), " page appear when accessing Microsoft's OAuth login flow, your Azure Active Directory user account might not have the necessary permissions to connect applications to your organization."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspaces/permissions/microsoft-need-admin-approval.png",
          alt: "Microsoft's \"Need admin approval\" screen"
        })
      }), "\n", _jsx(_components.p, {
        children: "Ask one of your Azure organization's admins to edit your user account permissions."
      }), "\n", _jsxs(_components.p, {
        children: ["You can read more about ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/active-directory/develop/active-directory-how-applications-are-added#who-has-permission-to-add-applications-to-my-azure-ad-instance",
          children: "Azure user permissions"
        }), " and ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/active-directory/manage-apps/configure-user-consent?pivots=portal#configure-user-consent-settings",
          children: "configuring user settings"
        }), " in Azure's documentation."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
